import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  FaCheck,
  FaFlask,
  FaRocket,
  FaArrowRight,
  FaSync,
  FaSitemap,
  FaBullseye,
  FaSearch,
  FaLayerGroup,
  FaFileAlt,
  FaListUl,
  FaAlignLeft,
} from 'react-icons/fa';
import './style.scoped.scss';
import ContactSalesModal from './ContactSalesModal';
import { ROUTES } from '../../../../assets/constants/strings';
import { isApiProduction } from '../../../../services/entitlements';
import { getApiUsage } from '../../../../services/axios';

// The API isn't sold as a price ladder — it has two states:
//  - Trial: fully self-serve. A one-time 100-call allowance to evaluate every
//    endpoint — explore in the browser (API Explorer, JWT) or create your own
//    key and call it from your app. Every API account starts here.
//  - Production/Enterprise: sales-led. When a customer wants to integrate we
//    scope volume, limits and terms, then provision them — no self-serve
//    checkout, no published price.
//
// This page is only ever seen by a signed-in API user who already holds Trial,
// so it speaks in "here's where you are" voice — current plan up front.
const TRIAL_FEATURES = [
  '100 API calls to evaluate',
  'Your own API key & secret',
  'Every endpoint + full documentation',
  'Live regulatory data',
];

const PRODUCTION_FEATURES = [
  'Volume & rate limits tailored to you',
  'Reliability & priority support',
  'SLA, DPA & security review',
  'Everything in the Trial',
];

// Who the API is for — shown as chips in the hero. Sets sales context.
const AUDIENCE = [
  'GRC platforms',
  'RegTech',
  'Compliance & legal',
  'Product teams',
];

// Benefit-led value props (the "why build on this", not the feature list).
const BENEFITS = [
  {
    icon: <FaSync />,
    title: 'Always-current content',
    text: 'Every document is versioned and status-tracked — Latest vs Superseded — so you never serve stale regulation.',
  },
  {
    icon: <FaSitemap />,
    title: 'Structured, not scraped',
    text: 'Documents, sections, glossaries and a four-tier taxonomy — ready to index, render or feed an LLM.',
  },
  {
    icon: <FaBullseye />,
    title: 'Down to the exact clause',
    text: 'Drill from a whole document to a single section’s content, with its tags, glossary terms and related sections.',
  },
  {
    icon: <FaSearch />,
    title: 'Search built in',
    text: 'Title lookup and full-text search across the entire corpus return matching, highlighted sections.',
  },
];

// The API's core flow, shown as four steps. Each names the real endpoint it
// maps to (current routes after the Taxonomy rename).
const ACCESS_STEPS = [
  {
    n: '01',
    icon: <FaLayerGroup />,
    title: 'Browse the taxonomy',
    text: 'Navigate the four tiers — regulator, category and below.',
    endpoint: 'GET /Taxonomies',
  },
  {
    n: '02',
    icon: <FaFileAlt />,
    title: 'Open a document',
    text: 'Pull a document’s metadata, version and status by its ID.',
    endpoint: 'GET /Document/{id}',
  },
  {
    n: '03',
    icon: <FaListUl />,
    title: 'List its sections',
    text: 'Retrieve every section that makes up the document.',
    endpoint: 'GET /Document/{id}/Sections',
  },
  {
    n: '04',
    icon: <FaAlignLeft />,
    title: 'Read the exact section',
    text: 'Get a single section’s full content, tags and related sections.',
    endpoint: 'GET /Section/{id}',
  },
];

const DEFAULT_TRIAL_CAP = 100;

const Plans = ({ user, token }) => {
  const [showContact, setShowContact] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [usage, setUsage] = useState({ used: 0, limit: 0, loading: true });

  const isProduction = isApiProduction(user);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setUsage({ used: 0, limit: 0, loading: false });
        return;
      }
      const now = new Date();
      const u = await getApiUsage(token, now.getFullYear(), now.getMonth() + 1);
      if (cancelled) return;
      setUsage({
        used: (u && u.used) || 0,
        limit: (u && u.callLimit) || 0,
        loading: false,
      });
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const openContact = (tierName) => {
    setSelectedTier(tierName);
    setShowContact(true);
  };

  const limit = usage.limit > 0 ? usage.limit : DEFAULT_TRIAL_CAP;
  const used = usage.loading ? 0 : usage.used;
  const remaining = Math.max(0, limit - used);
  const pct = Math.min(100, Math.round((used / limit) * 100));

  return (
    <div className='api-plans main-api-spacing'>
      {/* ── HERO — folds "current plan" + value prop into one ── */}
      <header className='apl-hero'>
        <div className='apl-hero__inner'>
          <div className='apl-hero__text'>
            <span className='apl-hero__pill'>
              <span className='apl-hero__dot' />
              {isProduction ? 'You’re on Enterprise' : 'You’re on the Trial'}
            </span>
            <h1 className='apl-hero__title'>
              {isProduction ? (
                <>
                  You’re integrated with <em>RegulationCity</em>
                </>
              ) : (
                <>
                  Build RegulationCity <em>into your product</em>
                </>
              )}
            </h1>
            <p className='apl-hero__sub'>
              {isProduction
                ? 'You’re on Enterprise — your API key and secret are live, with volume, limits and support tailored to you.'
                : 'Evaluate every endpoint on a one-time 100-call trial — in the browser or with your own key. When you’re ready to integrate, Enterprise scales with tailored volume, limits and support.'}
            </p>
            <div className='apl-hero__actions'>
              {isProduction ? (
                <>
                  <Link to={ROUTES.APIKeys} className='apl-btn apl-btn--teal'>
                    Manage API keys
                  </Link>
                  <Link to={ROUTES.APIDocs} className='apl-btn apl-btn--ghost'>
                    View documentation
                  </Link>
                </>
              ) : (
                <>
                  <Link to={ROUTES.APIExplorer} className='apl-btn apl-btn--teal'>
                    Open API Explorer
                  </Link>
                  <button
                    type='button'
                    className='apl-btn apl-btn--ghost'
                    onClick={() => openContact('Enterprise')}
                  >
                    Contact Us
                  </button>
                </>
              )}
            </div>
            <div className='apl-hero__built'>
              <span className='apl-hero__built-lbl'>Built for</span>
              {AUDIENCE.map((a) => (
                <span key={a} className='apl-chip'>
                  {a}
                </span>
              ))}
            </div>
          </div>

          {/* Side card: current-plan status + (trial) allowance meter */}
          <div className='apl-planbox'>
            <div className='apl-planbox__top'>
              <div>
                <p className='apl-planbox__label'>Your plan</p>
                <p className='apl-planbox__name'>
                  {isProduction ? 'Enterprise' : 'Trial'}
                </p>
              </div>
              <span className='apl-active'>
                <FaCheck /> Active
              </span>
            </div>

            {isProduction ? (
              <>
                <div className='apl-planbox__ent'>
                  <span className='apl-planbox__ent-val'>Uncapped</span>
                  <span className='apl-planbox__ent-lbl'>
                    volume &amp; limits tailored to you
                  </span>
                </div>
                <p className='apl-planbox__note'>
                  <b>You’re integrated.</b> Manage your keys and secrets, or talk
                  to us about changing your plan.
                </p>
                <Link to={ROUTES.APIKeys} className='apl-btn apl-btn--navy apl-btn--block'>
                  Manage API keys
                </Link>
              </>
            ) : (
              <>
                <div className='apl-meter__row'>
                  <span className='apl-meter__used'>
                    {usage.loading ? '—' : used.toLocaleString()}{' '}
                    <small>/ {limit.toLocaleString()}</small>
                  </span>
                  <span className='apl-meter__cap'>API calls used</span>
                </div>
                <div className='apl-meter'>
                  <div
                    className={`apl-meter__fill${pct >= 100 ? ' is-full' : ''}`}
                    style={{ width: `${usage.loading ? 0 : pct}%` }}
                  />
                </div>
                <p className='apl-planbox__note'>
                  <b>{remaining.toLocaleString()} calls left.</b> Ready to go to
                  production? Enterprise removes the cap and scales with you.
                </p>
                <button
                  type='button'
                  className='apl-btn apl-btn--navy apl-btn--block'
                  onClick={() => openContact('Enterprise')}
                >
                  Contact Us
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── PLAN CARDS ── */}
      <section className='apl-cards'>
        {/* TRIAL */}
        <div className={`apl-card apl-card--trial${!isProduction ? ' is-current' : ''}`}>
          {!isProduction && (
            <span className='apl-card__flag'>
              <FaCheck /> Your plan
            </span>
          )}
          <div className='apl-card__icon is-trial'>
            <FaFlask />
          </div>
          <h3 className='apl-card__name'>Trial</h3>
          <p className='apl-card__tag'>Evaluate the API</p>
          <p className='apl-card__price'>Free</p>
          <div className='apl-card__actions'>
            <Link to={ROUTES.APIExplorer} className='apl-btn apl-btn--ghost apl-btn--block'>
              Open API Explorer
            </Link>
            <Link to={ROUTES.APIDocs} className='apl-link'>
              View documentation <FaArrowRight />
            </Link>
          </div>
          <hr />
          <ul className='apl-feat'>
            {TRIAL_FEATURES.map((f, i) => (
              <li key={i}>
                <span className='apl-feat__tick'>
                  <FaCheck />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* ENTERPRISE */}
        <div className={`apl-card apl-card--ent${isProduction ? ' is-current' : ''}`}>
          {isProduction ? (
            <span className='apl-card__flag'>
              <FaCheck /> Your plan
            </span>
          ) : (
            <span className='apl-card__ribbon'>For integrations</span>
          )}
          <div className='apl-card__icon is-ent'>
            <FaRocket />
          </div>
          <h3 className='apl-card__name'>Enterprise</h3>
          <p className='apl-card__tag'>Integrate into your platform</p>
          <p className='apl-card__price'>
            Contact us <small>· tailored</small>
          </p>
          <div className='apl-card__actions'>
            {isProduction ? (
              <Link to={ROUTES.APIKeys} className='apl-btn apl-btn--navy apl-btn--block'>
                Manage API keys
              </Link>
            ) : (
              <button
                type='button'
                className='apl-btn apl-btn--navy apl-btn--block'
                onClick={() => openContact('Enterprise')}
              >
                Contact Us
              </button>
            )}
          </div>
          <hr />
          <ul className='apl-feat'>
            {PRODUCTION_FEATURES.map((f, i) => (
              <li key={i}>
                <span className='apl-feat__tick'>
                  <FaCheck />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className='apl-section'>
        <div className='apl-shead'>
          <span className='apl-shead__ey'>Why build on it</span>
          <h3>The regulatory content behind RegulationCity</h3>
          <p>
            Maintained by Revealr and structured for software, so your team ships
            features instead of wrangling source documents.
          </p>
        </div>
        <div className='apl-bgrid'>
          {BENEFITS.map((b, i) => (
            <div key={i} className='apl-benefit'>
              <span className='apl-benefit__icon'>{b.icon}</span>
              <h4 className='apl-benefit__title'>{b.title}</h4>
              <p className='apl-benefit__text'>{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── ACCESS FLOW ── */}
      <section className='apl-section'>
        <div className='apl-shead'>
          <span className='apl-shead__ey'>The core flow</span>
          <h3>Access any section of any document</h3>
          <p>
            Go from a regulator down to a single clause — each step is one call,
            and you can try every one free in the API Explorer.
          </p>
        </div>
        <div className='apl-fgrid'>
          {ACCESS_STEPS.map((s, i) => (
            <div key={i} className='apl-step'>
              <span className='apl-step__num'>{s.n}</span>
              <span className='apl-step__icon'>{s.icon}</span>
              <h4 className='apl-step__title'>{s.title}</h4>
              <p className='apl-step__text'>{s.text}</p>
              <code className='apl-step__ep'>{s.endpoint}</code>
            </div>
          ))}
        </div>
        <div className='apl-flow__cta'>
          <Link to={ROUTES.APIExplorer} className='apl-btn apl-btn--navy'>
            Try it in the API Explorer
          </Link>
          <Link to={ROUTES.APIDocs} className='apl-link'>
            Read the documentation <FaArrowRight />
          </Link>
        </div>
      </section>

      <p className='apl-footnote'>
        Questions about moving to Enterprise?{' '}
        <button type='button' className='apl-link' onClick={() => openContact('')}>
          Contact us
        </button>{' '}
        and we'll help you scope the right setup.
      </p>

      <ContactSalesModal
        show={showContact}
        tier={selectedTier}
        handleClose={() => setShowContact(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  token: state.user.user ? state.user.user.token : null,
});

export default connect(mapStateToProps, null)(Plans);
